import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCurrency } from '../../../../context/currencyContext';
import './RunDateRangeStatement.css';

const API_BASE = 'https://hms.backend.hiragroupcloud.com';

// -----------------------------------------------------------------------
// This component reads `statement.groupedByProvider` exactly as stored by
// the backend (same approach as ReprintButton.js) and renders it.
// Technician stamp (from EditPreferences) is shown in the header right.
// -----------------------------------------------------------------------

const RunDateRangeStatement = () => {
  const { formatAmount } = useCurrency();
  const [searchParams] = useSearchParams();

  const accountIds = useMemo(() => {
    const ids = searchParams.get('accountIds');
    return ids ? ids.split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id)) : [];
  }, [searchParams]);

  const providerIds = useMemo(() => {
    const ids = searchParams.get('providerIds');
    return ids ? ids.split(',').filter(id => id && id.trim() !== '') : [];
  }, [searchParams]);

  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];
  const selectedDate = searchParams.get('selectedDate') || null;
  const displayDateParam = selectedDate || date;
  const statementNote = decodeURIComponent(searchParams.get('statementNote') || '');
  const statementFooter = decodeURIComponent(searchParams.get('statementFooter') || '');
  const dontPrintEbilling = searchParams.get('dontPrintEbilling') === 'true';
  const dontPrintZeroBalance = searchParams.get('dontPrintZeroBalance') === 'true';
  const reprint = searchParams.get('reprint') === 'true';

  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runDate, setRunDate] = useState(null);
  const [labInfo, setLabInfo] = useState(null);
  const [stampUrl, setStampUrl] = useState('');
  const [printReady, setPrintReady] = useState(false);

  // ---- Pure display formatting only — no financial logic below this line ----

  const formatCredit = (amount) => {
    const safeAmount = Number(amount || 0);
    return safeAmount !== 0
      ? `-${Math.abs(safeAmount).toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "";
  };

  const removeLeadingZeros = (str) => {
    if (!str) return '';
    const result = String(str).replace(/^0+/, '');
    return result === '' ? '0' : result;
  };

  const getPatientName = (invoice) => {
    if (invoice.patientName) return invoice.patientName;
    if (invoice.patientFirstName || invoice.patientLastName) {
      return `${invoice.patientFirstName || ''} ${invoice.patientLastName || ''}`.trim();
    }
    return '';
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "Invalid Date";

    let dateObj;
    if (typeof dateStr === 'string') {
      if (dateStr.includes('T')) {
        dateObj = new Date(dateStr);
      } else {
        dateObj = new Date(dateStr + 'T00:00:00');
      }
    } else if (dateStr instanceof Date) {
      dateObj = dateStr;
    } else {
      return "Invalid Date";
    }

    if (isNaN(dateObj.getTime())) return "Invalid Date";

    const day = dateObj.getDate();
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const getSortDate = (dateStr) => {
    if (!dateStr) return new Date(2100, 0, 1).getTime();
    const normalized = typeof dateStr === 'string' && !dateStr.includes('T')
      ? `${dateStr}T00:00:00`
      : dateStr;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? new Date(2100, 0, 1).getTime() : d.getTime();
  };

  const getTruncatedDescription = (transaction) => {
    if (!transaction) return '';

    const description = transaction.displayDescription || transaction.description || '';

    if (description.includes('for Invoices:')) {
      const [label, listPart] = description.split('for Invoices:');
      if (listPart) {
        const invoiceNumbers = listPart.trim().split(',').map(num => num.trim());
        if (invoiceNumbers.length > 5) {
          const displayedInvoices = invoiceNumbers.slice(0, 5).join(', ');
          const remaining = invoiceNumbers.length - 5;
          return `${label.trim()} for Invoices: ${displayedInvoices} ... (and ${remaining} more)`;
        }
      }
    }

    return description;
  };

  const formatAmountAlways = (amount) => {
    const value = Number(amount || 0);
    return value.toLocaleString("en-GB", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // -------------------------------------------------------------------------
  // Fetch the actual lab details for the lab whose statement is running.
  // Reads from localStorage first, falls back to API only if storage is empty.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const safeParse = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const buildLabInfoFromStorage = () => {
      const activeLabId = localStorage.getItem("activeLabId") || localStorage.getItem("userLabId") || "";

      const storedLabInfo = safeParse(localStorage.getItem("labInfo"));
      const storedLabDetails = safeParse(localStorage.getItem("labDetails"));
      const storedUserData = safeParse(localStorage.getItem("userData"));
      const storedAddress = safeParse(localStorage.getItem("userAddress"));

      const storedLabName = localStorage.getItem("labName") || "";
      const storedLabEmail =
        localStorage.getItem("labEmail") ||
        localStorage.getItem("userEmail") ||
        "";

      const getField = (field) => {
        return (
          storedLabInfo?.[field] ||
          storedLabDetails?.[field] ||
          storedUserData?.[field] ||
          ""
        );
      };

      const resolvedEmail =
        getField("labEmail") ||
        storedLabEmail ||
        getField("email") ||
        getField("statementEmail") ||
        "";

      const merged = {
        labId: activeLabId,
        labName: getField("labName") || storedLabName || "",
        labEmail: resolvedEmail,
        address: getField("address") || storedAddress?.address || "",
        address2: getField("address2") || storedAddress?.address2 || "",
        city: getField("city") || storedAddress?.city || "",
        state: getField("state") || getField("stateProvince") || storedAddress?.state || "",
        zipCode: getField("zipCode") || getField("zipPostal") || storedAddress?.zipPostal || "",
        country: getField("country") || storedAddress?.country || "",
        telephone: getField("telephone") || "",
        // stamp (fallback if localStorage "technicianStamp" is empty)
        technicianStamp:
          storedLabInfo?.technicianStamp ||
          storedLabInfo?.preferences?.technicianStamp ||
          storedUserData?.technicianStamp ||
          storedUserData?.preferences?.technicianStamp ||
          "",
      };

      const hasAny =
        merged.labName ||
        merged.labEmail ||
        merged.address ||
        merged.city ||
        merged.telephone;
      return hasAny ? merged : null;
    };

    const fetchLabInfo = async () => {
      const fromStorage = buildLabInfoFromStorage();
      if (fromStorage) {
        setLabInfo(fromStorage);
        return;
      }

      // Fallback to API
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${API_BASE}/api/auth/users`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success && response.data.users.length > 0) {
          const activeLabId = localStorage.getItem("activeLabId") || localStorage.getItem("userLabId") || "";
          const matching =
            response.data.users.find(
              (u) =>
                String(u.labId || u.userLabId || "") === String(activeLabId)
            ) || response.data.users[0];
          setLabInfo(matching);
        }
      } catch (err) {
        console.error("Lab info fetch error:", err);
      }
    };
    fetchLabInfo();
  }, []);

  // -------------------------------------------------------------------------
  // Technician stamp (same stamp uploaded in EditPreferences)
  // -------------------------------------------------------------------------
  useEffect(() => {
    const resolveStamp = () => {
      const raw =
        localStorage.getItem('technicianStamp') ||
        labInfo?.technicianStamp ||
        labInfo?.preferences?.technicianStamp ||
        '';

      if (!raw) {
        setStampUrl('');
        return;
      }

      if (/^https?:\/\//i.test(raw)) {
        setStampUrl(raw);
        return;
      }

      let cleanPath = raw;
      if (!cleanPath.startsWith('/')) {
        cleanPath = `/uploads/stamps/${cleanPath}`;
      } else if (!cleanPath.startsWith('/uploads')) {
        cleanPath = `/uploads/stamps/${cleanPath.replace(/^\//, '')}`;
      }
      setStampUrl(`${API_BASE}${cleanPath}`);
    };

    resolveStamp();
    window.addEventListener('stampUpdated', resolveStamp);
    return () => window.removeEventListener('stampUpdated', resolveStamp);
  }, [labInfo]);

  // Fetch statements — reads exactly what's stored, no re-derivation.
  useEffect(() => {
    const fetchStatements = async () => {
      if (accountIds.length === 0) {
        setError('No valid account IDs provided.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem('token');

        const params = {
          accountIds: accountIds.join(','),
          date,
          dontPrintEbilling,
          dontPrintZeroBalance
        };

        if (providerIds.length > 0) {
          params.providerIds = providerIds.join(',');
        }

        console.log('📊 Fetching stored statements with params:', params);

        // If reprint flag is true, use stored-statement endpoint,
        // otherwise use run-date-range endpoint.
        let apiUrl = `${API_BASE}/api/statements/run-date-range`;
        if (reprint) {
          apiUrl = `${API_BASE}/api/statements/stored-statement`;
        }

        const response = await axios.get(apiUrl, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        let statementsData = [];
        let runDateValue = date;

        if (response.data.success) {
          if (reprint) {
            if (response.data.data && response.data.data.statements) {
              statementsData = response.data.data.statements;
              runDateValue = response.data.data.runDate || date;
            }
          } else {
            if (Array.isArray(response.data.statements)) {
              statementsData = response.data.statements;
              runDateValue = response.data.runDate || date;
            }
          }
        }

        if (!statementsData || statementsData.length === 0) {
          setError(response.data.message || 'No statements data available');
          setStatements([]);
          setIsLoading(false);
          return;
        }

        // Optional provider filtering — display-only.
        const filteredStatements = providerIds.length === 0
          ? statementsData
          : statementsData
              .map((statement) => {
                const filteredGrouped = {};
                Object.entries(statement.groupedByProvider || {}).forEach(([key, group]) => {
                  if (key === 'general' || providerIds.includes(String(group.providerId))) {
                    filteredGrouped[key] = group;
                  }
                });
                if (Object.keys(filteredGrouped).length === 0) return null;
                return { ...statement, groupedByProvider: filteredGrouped };
              })
              .filter(Boolean);

        setStatements(filteredStatements);
        setRunDate(runDateValue);
        setPrintReady(true);

      } catch (err) {
        console.error('Fetch error:', err);
        const errorMessage = err.response?.data?.message ||
                            err.response?.data?.error ||
                            err.message ||
                            "An error occurred while fetching statements.";
        setError(errorMessage);
        setStatements([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatements();
  }, [accountIds, date, dontPrintEbilling, dontPrintZeroBalance, providerIds, reprint]);

  const handlePrint = () => window.print();

  const renderNote = (note) => {
    if (!note) return null;
    return note.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  // Builds the sorted row list for a single stored provider group, reading
  // its invoices/transactions exactly as they were saved — no recompute.
  const buildProviderRows = (group, previousRunDateForAccount) => {
    const rows = [];
    const invoices = group.invoices || [];
    const prevInvoices = invoices.filter((inv) => inv.isPreviousBalance);
    const currentInvoices = invoices.filter((inv) => !inv.isPreviousBalance);

    if (prevInvoices.length > 0) {
      const prevTotal = prevInvoices.reduce(
        (sum, inv) => sum + Number(inv.debit ?? inv.total ?? 0),
        0
      );
      rows.push({
        sortDate: getSortDate(previousRunDateForAccount),
        jsx: (
          <tr key={`prev-balance-${group.providerId}`}>
            <td>{formatDisplayDate(previousRunDateForAccount)}</td>
            <td><strong>Previous Balance</strong></td>
            <td>{formatAmountAlways(prevTotal)}</td>
            <td></td>
          </tr>
        )
      });
    }

    currentInvoices.forEach((invoice) => {
      const patientName = getPatientName(invoice);
      const invoiceDate = invoice.deliveryDate || invoice.invoiceDate;
      const formattedInvoiceNumber =
        invoice.formattedInvoiceNumber || removeLeadingZeros(invoice.invoiceNumber);

      rows.push({
        sortDate: getSortDate(invoiceDate),
        jsx: (
          <tr key={`inv-${invoice.invoiceNumber}`}>
            <td>{formatDisplayDate(invoiceDate)}</td>
            <td>Case Invoice {formattedInvoiceNumber} / {patientName}</td>
            <td>{formatAmountAlways(invoice.debit ?? invoice.total ?? 0)}</td>
            <td></td>
          </tr>
        )
      });
    });

    (group.transactions || []).forEach((trans, idx) => {
      rows.push({
        sortDate: getSortDate(trans.date),
        jsx: (
          <tr key={`trans-${group.providerId}-${idx}`} className="sub-row">
            <td>{formatDisplayDate(trans.date)}</td>
            <td>{getTruncatedDescription(trans)}</td>
            <td></td>
            <td>{formatCredit(trans.amount)}</td>
          </tr>
        )
      });
    });

    rows.sort((a, b) => a.sortDate - b.sortDate);
    return rows;
  };

  if (isLoading) {
    return (
      <div className="rds-statement-root">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading statements for {formatDisplayDate(displayDateParam)}...</p>
          <p className="text-muted small">Account: {accountIds.length === 1 ? accountIds[0] : "Multiple Accounts"}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rds-statement-root">
        <div className="alert alert-danger m-4" role="alert">
          <h4 className="alert-heading">Error Loading Statements</h4>
          <p>{error}</p>
          <hr />
          <div className="mb-3">
            <p><strong>Search Criteria:</strong></p>
            <ul className="mb-0">
              <li>Date: {date} ({formatDisplayDate(date)})</li>
              <li>Account: {accountIds.length === 1 ? accountIds[0] : "Multiple Accounts"}</li>
              <li>Don't Print Ebilling: {dontPrintEbilling.toString()}</li>
              <li>Don't Print Zero Balance: {dontPrintZeroBalance.toString()}</li>
            </ul>
          </div>
          <button className="btn btn-outline-danger" onClick={() => window.location.reload()}>Try Again</button>
          <button className="btn btn-outline-secondary ms-2" onClick={() => window.close()}>Close Window</button>
        </div>
      </div>
    );
  }

  if (statements.length === 0) {
    return (
      <div className="rds-statement-root">
        <div className="alert alert-warning m-4" role="alert">
          <h4 className="alert-heading">No Statements Found</h4>
          <p>No statements found for the selected criteria.</p>
          <div className="mt-3">
            <p><strong>Search Criteria:</strong></p>
            <ul className="mb-0">
              <li>Date: {date} ({formatDisplayDate(date)})</li>
              <li>Account: {accountIds.length === 1 ? accountIds[0] : "Multiple Accounts"}</li>
            </ul>
          </div>
          <button className="btn btn-outline-warning mt-3" onClick={() => window.close()}>Close Window</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rds-statement-root">
      <div className="statement-container">
        <div className="header-controls">
          <div className="button-container">
            <button className="print-button" onClick={handlePrint} disabled={!printReady}>Print</button>
          </div>
        </div>

        {statements.map((statement, index) => {
          const providerList = Object.values(statement.groupedByProvider || {})
            .filter(
              (group) =>
                (group.invoices && group.invoices.length > 0) ||
                (group.transactions && group.transactions.length > 0)
            )
            .sort((a, b) => {
              if (a.providerId === 'general') return -1;
              if (b.providerId === 'general') return 1;
              return (a.providerName || '').localeCompare(b.providerName || '');
            });

          const providerCount = providerList.length;
          const previousRunDateForAccount = statement.previousRunDate || statement.lastRunDate;

          return (
            <div
              key={`${statement.accountId}-${index}`}
              className="statement-section"
              style={{ pageBreakBefore: index > 0 ? "always" : "auto" }}
            >
              <div className="header-section">
                <div className="lab-info">
                  <strong>{labInfo?.labName || "Hira Dental Laboratory"}</strong>
                  <br />
                  {labInfo?.address || "02 Ulverscroft Road"}<br />
                  {[
                    labInfo?.city,
                    labInfo?.state,
                    labInfo?.zipCode || labInfo?.zipPostal,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Coventry, CV3 5EZ"}
                  <br />
                  {labInfo?.telephone
                    ? `Tel: ${labInfo.telephone}`
                    : "Tel: 024 7642 2500"}
                  <br />
                </div>
                <div className="header-right">
                  <div className="statement-info">
                    <div className="statement-date">
                      <strong>Statement for {formatDisplayDate(statement.displayDate || runDate)}</strong>
                    </div>
                    <div className="contact-info">
                      {labInfo?.labEmail || "info@hiradentallab.com"}
                    </div>
                  </div>
                  <div className="logo-container-right">
                    {stampUrl && (
                      <img
                        className="header-stamp"
                        src={stampUrl}
                        alt="Technician Stamp"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              <h3 className="statement-title">
                Statement for {formatDisplayDate(statement.displayDate || runDate)}
              </h3>

              <div className="client-payment-wrapper">
                <div className="client-info">
                  <strong>{statement.accountHolder || "Account Holder Name"}</strong>
                  <br />
                  {statement.address && statement.address !== "Address not available" ? statement.address : "02 Ulverscroft Road"}
                  <br />
                  {[statement.city, statement.state, statement.zip].filter(Boolean).join(", ") || "Coventry, CV4 5EZ"}
                  <br />
                  {statement.telephone && statement.telephone !== "Not available" ? `Tel: ${statement.telephone}` : "Tel: 024 7642 2500"}
                  <br />
                  {statement.email && statement.email !== "Email not available" ? `Email: ${statement.email}` : "Email: accounts@hiradentallab.com"}
                </div>
                <div className="payment-details">
                  {statementNote ? renderNote(statementNote) : (
                    statement.note ? statement.note.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line}
                        <br />
                      </React.Fragment>
                    )) : (
                      <div>
                        <div>Hira Dental Laboratory</div>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="table-wrapper">
                <table className="statement-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {providerList.map((group) => {
                      const rows = buildProviderRows(group, previousRunDateForAccount);
                      const displayProviderName =
                        group.providerId === 'general' ? 'General' : group.providerName;

                      return (
                        <React.Fragment key={group.providerId}>
                          <tr className="provider-row">
                            <td colSpan="4"><strong>{displayProviderName}</strong></td>
                          </tr>
                          {rows.length > 0 ? (
                            rows.map((row) => row.jsx)
                          ) : (
                            <tr>
                              <td colSpan="4" style={{ textAlign: 'center', fontStyle: 'italic' }}>
                                No transactions for this provider
                              </td>
                            </tr>
                          )}
                          {providerCount > 1 && Math.abs(Number(group.newBalance || 0)) > 0.001 && (
                            <tr className="subtotal-row">
                              <td colSpan="2"><strong>Subtotal</strong></td>
                              {group.providerId === 'general' ? (
                                <>
                                  <td></td>
                                  <td>{formatCredit(group.newBalance || 0)}</td>
                                </>
                              ) : (
                                <>
                                  <td>{formatAmountAlways(group.newBalance || 0)}</td>
                                  <td></td>
                                </>
                              )}
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="aging-summary">
                <table className="aging-table">
                  <thead>
                    <tr>
                      <th>1-30 Days</th>
                      <th>31-60 Days</th>
                      <th>61-90 Days</th>
                      <th>91+ Days</th>
                      <th>New Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{formatAmountAlways(statement.oneTo30Days || 0)}</td>
                      <td>{formatAmountAlways(statement.thirtyOneTo60Days || 0)}</td>
                      <td>{formatAmountAlways(statement.sixtyOneTo90Days || 0)}</td>
                      <td>{formatAmountAlways(statement.ninetyPlusDays || 0)}</td>
                      <td>{formatAmountAlways(statement.newBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="statement-footer">
                <div>{statement.accountHolder}</div>
                <div>{statementFooter || statement.footer || 'Thank you for your business'}</div>
                <div>Page {index + 1} of {statements.length}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RunDateRangeStatement;